import { Fragment, useMemo } from "react";
import { useIntl } from "react-intl";
import { TabGroup, TabList, Tab, TabPanels, TabPanel } from '@headlessui/react';
import classNames from "classnames";
import { WebChatBanner, WebChatPreviewOnly, WebChatTabWidgets, WebChatThemeProps } from "@42maru/webchat";
import { API_HOST, WEBCHAT_HOST, WEBCHAT_SOCKET_PATH } from "@/core/variables";
import { ScenarioProperties } from "@/model";
import { previewDummyMessages } from "@/util/helper/webchatThemeHelper";
import { useWebchatPreviewMode } from "@/hook";


const CONNECTION_CONFIG = {
    apiHost: API_HOST,
    socketHost: WEBCHAT_HOST,
    socketPath: WEBCHAT_SOCKET_PATH,
    method: "socket" as const,
    test: true,
    hideDebugMessage: true
};

interface WebchatSettingPreviewProps {
    scenarioId: string;
    theme: WebChatThemeProps;
    properties?: ScenarioProperties;
}

const WebchatSettingPreview: React.FC<WebchatSettingPreviewProps> = (props) => {
    const { scenarioId, theme, properties } = props;

    const intl = useIntl();
    const { previewMode, setHomeMode, setChatMode } = useWebchatPreviewMode();

    const previewTheme = useMemo(() => {
        const clone = structuredClone(theme);
        clone.showCloseButton = false;
        if (previewMode.chatModeOption === "bottom-menu" && clone.bottomMenu) {
            clone.bottomMenu.open = true;
        }
        return clone;
    }, [theme, previewMode]);

    const chatModeKeyToRerender = useMemo(() => {
        if (previewMode.mode === "chat" && previewMode.chatModeOption === "bottom-menu" && previewTheme.bottomMenu?.enable) {
            return "chat-bottom-menu";
        }
        return "chat-default";
    }, [previewMode, previewTheme]);

    if (previewMode.mode === "banner") {
        return (
            <div className="webchat-preview-container pt-5 rouned-[12px] shadow-[0px_5px_20px_0px_rgba(140,140,140,1.00)]">
                <div className="mx-3 mb-4 px-3 w-webchat">
                    {intl.formatMessage({ id: "i09008" })}
                </div>
                <BannerPreview theme={theme}
                               displayType={previewMode.bannerMode ?? "desktop"}
                />
            </div>
        );
    }
    const isHomeMode = previewMode.mode === "home";
    return (
        <TabGroup as="div"
                  selectedIndex={isHomeMode ? 0 : 1}
                  aria-hidden="true"
                  className="webchat-preview-container p-3 flex flex-col gap-3"
                  onChange={index => {
                      if (index === 0) {
                          setHomeMode();
                      } else {
                          setChatMode();
                      }
                  }}
        >
            <TabList data-testid="webchat-preview-tablist"
                     className="w-full flex items-center gap-3"
            >
                <Tab data-testid="webchat-preview-home-tab"
                     className="tab-btn-sm text-black-500 font-[400] border-transparent aria-selected:text-chat42 aria-selected:font-[600] aria-selected:border-chat42"
                >
                    {intl.formatMessage({ id: "b0060" })}
                </Tab>
                <Tab data-testid="webchat-preview-chat-tab"
                     className="tab-btn-sm text-black-500 font-[400] border-transparent aria-selected:text-chat42 aria-selected:font-[600] aria-selected:border-chat42"
                >
                    {intl.formatMessage({ id: "i09007" })}
                </Tab>
            </TabList>

            <div className="relative w-full min-h-0">
                <div className="absolute top-0 left-0 px-3 py-0.5 text-white text-[13px] font-[400] bg-black/50 rounded-tl-[16px] overflow-hidden z-10">
                    {intl.formatMessage({ id: "i09009" })}
                </div>

                <div className="webchat-preview-wrapper">
                    <TabPanels as={Fragment}>
                        <TabPanel as={Fragment}>
                            <WebChatTabWidgets scenarioId={scenarioId}
                                               theme={previewTheme}
                                               clientType="chat_cover"
                                               footerType="cm"
                                               disableAnimation
                                               disableRefresh
                                               integrations={{}}
                                               locale={properties?.language ?? "ko"}
                                               connectionConfig={CONNECTION_CONFIG}
                                               setWebChatAppView={view => {
                                                   if (view === "intro") {
                                                       setHomeMode();
                                                   } else {
                                                       setChatMode();
                                                   }
                                               }}
                            />
                        </TabPanel>
                        <TabPanel as={Fragment}>
                            <WebChatPreviewOnly key={chatModeKeyToRerender}
                                                scenarioId={scenarioId}
                                                theme={previewTheme}
                                                className="size-full"
                                                clientType="chat_only"
                                                footerType="cm"
                                                disableInteraction
                                                showBackButton
                                                isChatAlignBottom={false}
                                                locale={properties?.language ?? "ko"}
                                                previewMessage={previewDummyMessages(properties?.language)}
                                                connectionConfig={CONNECTION_CONFIG}
                                                onClickExpandButton={() => {}}
                            />
                        </TabPanel>
                    </TabPanels>
                </div>
            </div>
        </TabGroup>
    );
};


interface BannerPreviewProps {
    theme: WebChatThemeProps;
    displayType: "mobile" | "desktop";
}

const BannerPreview: React.FC<BannerPreviewProps> = (props) => {
    const { theme, displayType } = props;

    const bannerSettings = displayType === "desktop" ? theme.embeddedSettings! : theme.embeddedSettings!.mobile!;
    const isLeftPosition = bannerSettings.buttonReferencePosition === "left";

    const {
        buttonWidth: width,
        buttonHeight: height,
        buttonLeft: left,
        buttonRight: right,
        buttonBottom: bottom,
    } = bannerSettings;

    const axisStyle = {
        vertical: {
            height: bottom,
            ...(isLeftPosition
                ? { left: left + width / 2 }
                : { right: right + width / 2 }),
        },
        horizontal: {
            bottom: bottom + height / 2,
            ...(isLeftPosition
                ? { left: 0, width: left }
                : { right: 0, width: right }),
        }
    };
    
    return (
        <div data-testid="banner-preview"
             className="relative overflow-hidden"
        >
            <div data-direction="bottom"
                 className="absolute bottom-0 flex items-center"
                 style={axisStyle.vertical}
            >
                <div className="w-0.5 h-full bg-black-400" />
                <span className={classNames(
                          "absolute text-[14px] font-[400] text-black-500 tabular-nums",
                          isLeftPosition ? "left-3.5" : "right-3.5"
                      )}
                >
                    {bottom}
                </span>
            </div>

            <div data-direction={isLeftPosition ? "left" : "right"}
                 className="absolute flex flex-col items-center"
                 style={axisStyle.horizontal}
            >
                <span className="mb-1.5 text-[14px] font-[400] text-black-500 tabular-nums">
                    {isLeftPosition ? left : right}
                </span>
                <div className="h-0.5 w-full bg-black-400" />
            </div>

            <div className="h-[250px] w-full inset-0">
                <WebChatBanner hasConnectError={false}
                               bannerPosition="absolute"
                               style={{ whiteSpace: "nowrap" }}
                               bannerSettings={bannerSettings}
                               showBannerText={!!bannerSettings.bannerText}
                />
            </div>
        </div>
    );
};

export default WebchatSettingPreview;
